/**
 * localComponentGate — the SINGLE rule for whether a local GPU-component
 * provider card (Fooocus, ComfyUI / comfyui-sd, Deforum, AnimateDiff, ...) is
 * selectable in a provider picker.
 *
 * Standard behaviour for ALL local components (RULE #-2: one implementation):
 *
 *   - status unknown (first poll pending / cloud mode) → selectable
 *     (conservative — never flicker a working provider to disabled).
 *   - NOT installed                                     → disabled, reason
 *     'not_installed' (show an "install it in Settings" hint).
 *   - installed (running OR idle)                       → selectable.
 *
 * A component only needs to be INSTALLED to be selectable: the backend starts
 * it on demand and VRAM-manages it (ComponentLifecycle + VRAMManager evict
 * other idle GPU components, exactly like Fooocus). Requiring it to be already
 * *running* was the bug that hid installed-but-idle providers (ComfyUI never
 * appeared, Deforum was filtered out of the video list).
 */

/** Minimal status shape — works for both ComponentStatus and video availability. */
export interface InstallableStatus {
  is_installed?: boolean;
}

export interface LocalComponentGate {
  /** Whether the provider card should be selectable. */
  selectable: boolean;
  /** Why it is not selectable, if applicable. */
  reason: 'not_installed' | null;
}

/**
 * Compute the selectability gate for a local component from its status.
 *
 * @param status - The component status, or null when unknown (still loading
 *                 or no local backend to poll).
 */
export function gateLocalComponent(
  status: InstallableStatus | null | undefined
): LocalComponentGate {
  // Unknown — be conservative and keep the card selectable.
  if (status === null || status === undefined) {
    return { selectable: true, reason: null };
  }
  if (status.is_installed !== true) {
    return { selectable: false, reason: 'not_installed' };
  }
  // Installed → selectable regardless of is_running (auto-starts on demand).
  return { selectable: true, reason: null };
}
